import { jsx } from "panel";
import { BannerEl } from "common/components/banner";
import { BannerType } from "common/components/banner/types";
import { ButtonEl } from "common/components/button";
import { ButtonSize, ButtonTheme } from "common/components/button/constants";
import { InputEl } from "common/components/input";
import { ModalEl } from "common/components/modal";
import { SelectEl } from "common/components/select";
import { SpinnerEl } from "common/components/spinner";
import { SpinnerColor } from "common/components/spinner/types";
import { SvgIconEl } from "common/components/svg-icon";
import { TooltipEl } from "common/components/tooltip";
import { DisplayType, Placement } from "common/components/tooltip/types";
import { FileUploaderEl } from "common/components/file-uploader";
import { ExpiresModeOptions, ExpiresUnit } from "common/types/reports/service-accounts";
import { Permission } from "common/report/constants";
import { capitalize, pluralize } from "common/util/string";
import { assertUnreachable } from "common/util/typescript";
import { AutocompleteType } from "common/components/input/types";
import { CopyToClipboardEl } from "../copy-to-clipboard";
import { Tab } from "./types";
import { ProjectInviteModalContentEl } from "./project";
export function template() {
  const { error, isLoading, serviceAccount, userFile, userRows } = this.state;
  const FooterButtons = () => {
    return /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-footer-buttons"
    }, /* @__PURE__ */ jsx(ButtonEl, {
      sel: ".mp-modal-button",
      attrs: {
        theme: ButtonTheme.Primary,
        disabled: isLoading || !this.attr(`open`) || this.isOverSeatCount()
      },
      on: { click: this.helpers.clickedSave }
    }, "Invite")));
  };
  const EmailColumn = ({ row, idx }) => {
    if (this.isServiceAccount()) {
      return /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
        sel: ".invite-users-title"
      }, "Name"), /* @__PURE__ */ jsx("div", {
        sel: ".invite-users-input-container"
      }, /* @__PURE__ */ jsx(InputEl, {
        sel: ".invite-users-service-account-name",
        attrs: {
          "error-message": row.nameErrorMsg,
          "validation-status": this.helpers.serviceAccountNameValidationStatus(row)
        },
        props: {
          value: row.name
        },
        on: {
          input: (ev) => this.helpers.updateServiceAccountName(ev, row)
        }
      })));
    }
    const descriptor = row.email && row.email.length ? `a valid` : `an`;
    return /* @__PURE__ */ jsx(null, null, !idx && /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-title"
    }, "Person"), /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-input-container"
    }, /* @__PURE__ */ jsx(InputEl, {
      sel: ".invite-users-input.invite-users-email-input",
      attrs: {
        type: `text`,
        autocomplete: AutocompleteType.InviteUsersAutocompleteOff,
        placeholder: `Enter Name or Email...`
      },
      props: {
        value: row.email
      },
      on: {
        input: (ev) => this.helpers.updateUserEmail(ev, row),
        focus: this.helpers.clearErrors,
        blur: (ev) => this.helpers.updateEmailError(ev, row)
      }
    }), (row.emailError || row.emailInfo) && row === userRows.find((userRow) => userRow.email === row.email) && /* @__PURE__ */ jsx(TooltipEl, {
      attrs: {
        "always-show": true,
        type: row.emailInfo ? DisplayType.Default : DisplayType.Error,
        placement: Placement.Left
      }
    }, row.emailErrorMsg || `Please enter ${descriptor} email address`)));
  };
  const ProjectRoleColumn = ({ row, idx }) => {
    return /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-column"
    }, !idx && /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-title"
    }, "Project Role"), /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-input-container"
    }, /* @__PURE__ */ jsx(SelectEl, {
      attrs: {
        "selected-option": row.projectRole ? capitalize(row.projectRole) : `Select role`,
        "menu-height": 300,
        size: ButtonSize.Large
      },
      props: {
        sections: this.helpers.getProjectRoleSections()
      },
      on: {
        change: (ev) => this.helpers.selectProjectRole(ev, row),
        focus: this.helpers.clearErrors
      }
    }), row.projectRoleError && row === userRows.find((row2) => row2.projectRoleError) && /* @__PURE__ */ jsx(TooltipEl, {
      attrs: {
        "always-show": true,
        type: DisplayType.Error,
        placement: Placement.Right
      }
    }, "Please select a role"))), this.isServiceAccount() && /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-column.invite-users-expires-column"
    }, /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-expires-container"
    }, /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-title"
    }, "Expires"), (row == null ? void 0 : row.expires) ? /* @__PURE__ */ jsx("div", {
      sel: "expires-form"
    }, /* @__PURE__ */ jsx(InputEl, {
      sel: ".invite-users-expires-interval-input",
      attrs: { type: `number` },
      props: { value: String(row.expires.interval) },
      on: { input: (ev) => this.helpers.updateExpiresInterval(ev, row) }
    }), /* @__PURE__ */ jsx(SelectEl, {
      sel: ".invite-users-expires-unit-selector",
      attrs: {
        "menu-width": 100,
        options: Object.values(ExpiresUnit),
        "selected-option": row.expires.unit
      },
      on: { change: (ev) => this.helpers.selectExpiresUnit(ev, row) }
    })) : /* @__PURE__ */ jsx(SelectEl, {
      sel: ".invite-users-expires-mode-selector",
      attrs: {
        "menu-width": 100,
        options: Object.values(ExpiresModeOptions),
        "selected-option": ExpiresModeOptions.Never
      },
      on: { change: (ev) => this.helpers.selectExpirationMode(ev, row) }
    }))));
  };
  const TeamsColumn = ({ row, idx }) => {
    const numTeams = row.teamNames.length;
    const teamPlural = pluralize(`team`, numTeams);
    return /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-column"
    }, /* @__PURE__ */ jsx(null, null, !idx && /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-title"
    }, "Teams"), /* @__PURE__ */ jsx(SelectEl, {
      attrs: {
        "selected-option": numTeams ? `${numTeams} ${teamPlural}` : `Select teams`,
        multiple: true,
        "show-select-all": true,
        size: ButtonSize.Large
      },
      props: {
        sections: this.helpers.getTeamSections(row)
      },
      on: {
        change: (ev) => this.helpers.toggleTeamSelected(ev, row),
        selectAll: (ev) => this.helpers.toggleAllTeamsSelected(ev, row)
      }
    })));
  };
  const ProjectDowngrade = ({ row, idx }) => {
    const downgradedProjectsString = this.helpers.getDowngradedProjectsString(row);
    const allDowngradedProjectsString = this.helpers.getAllDowngradedProjectsString(row);
    return /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-row-error"
    }, /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-error-title"
    }, "You do not have permission to assign the Project Role of Owner for all the selected projects. Admin will be assigned for ", row.projectIdsToDowngrade.length, " projects:"), row.seeAll ? /* @__PURE__ */ jsx("div", {
      sel: ".projects-list"
    }, allDowngradedProjectsString) : /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
      sel: ".project-list"
    }, downgradedProjectsString), downgradedProjectsString.length < allDowngradedProjectsString.length && /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-see-all",
      on: { click: () => this.helpers.seeAllDowngradedProjects(idx) }
    }, "See All")));
  };
  const ProjectSelections = ({ row, idx }) => {
    const isSingleProject = !!this.getProjectIdIfSingleProject();
    const numProjects = row.projectIds.length;
    const projectPlural = pluralize(`project`, numProjects);
    return !isSingleProject && /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-column",
      style: { width: `250px` }
    }, /* @__PURE__ */ jsx(null, null, !idx ? /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-title"
    }, "Projects") : null, /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-input-container"
    }, /* @__PURE__ */ jsx(SelectEl, {
      style: { width: `250px` },
      attrs: {
        "selected-option": numProjects ? `${numProjects} ${projectPlural}` : `Select projects`,
        multiple: true,
        "show-select-all": true,
        size: ButtonSize.Large,
        "menu-width": 250
      },
      props: {
        sections: this.helpers.getProjectSections(row)
      },
      on: {
        change: (ev) => this.helpers.toggleProjectSelected(ev, row),
        selectAll: (ev) => this.helpers.toggleAllProjectsSelected(ev, row),
        focus: this.helpers.clearErrors
      }
    })), row.projectError && row === userRows.find((row2) => row2.projectError) ? /* @__PURE__ */ jsx(TooltipEl, {
      attrs: {
        "always-show": true,
        type: DisplayType.Error,
        placement: Placement.Top
      }
    }, "Please select at least one project") : null));
  };
  const InviteUsersRow = ({ row, idx }) => {
    return /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-row"
    }, /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-column"
    }, /* @__PURE__ */ jsx(EmailColumn, {
      row,
      idx
    })), /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-column"
    }, !idx && /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-title"
    }, "Organization Role"), /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-input-container"
    }, /* @__PURE__ */ jsx(SelectEl, {
      attrs: {
        "selected-option": row.orgRole ? capitalize(row.orgRole) : `Select role`,
        "menu-height": 350,
        size: ButtonSize.Large
      },
      props: {
        sections: this.helpers.getOrgRoleSections()
      },
      on: {
        change: (ev) => this.helpers.selectOrgRole(ev, row),
        focus: this.helpers.clearErrors
      }
    })), row.orgRoleError && row === userRows.find((row2) => row2.orgRoleError) && /* @__PURE__ */ jsx(TooltipEl, {
      attrs: {
        "always-show": true,
        type: DisplayType.Error,
        placement: Placement.Top
      }
    }, "Please select a role")), this.inviteType === Tab.Org && /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx(ProjectSelections, {
      row,
      idx
    }), /* @__PURE__ */ jsx(ProjectRoleColumn, {
      row,
      idx
    })), this.inviteType === Tab.Team && /* @__PURE__ */ jsx(TeamsColumn, {
      row,
      idx
    }), userRows.length > 1 && /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-remove-button",
      on: { click: () => this.helpers.clickedDeleteButton(row) }
    }, /* @__PURE__ */ jsx(SvgIconEl, {
      attrs: { icon: `x` }
    }))), row.projectIdsToDowngrade.length ? /* @__PURE__ */ jsx(ProjectDowngrade, {
      row,
      idx
    }) : null);
  };
  const BulkInvite = () => {
    return /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-bulk-add-users"
    }, /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-bulk-add-users-title"
    }, "File name"), /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-bulk-add-file-uploader"
    }, /* @__PURE__ */ jsx(FileUploaderEl, {
      attrs: {
        accept: `text/csv`,
        label: `Select CSV File`,
        "completed-filename": userFile && userFile.name ? userFile.name : ``,
        "enable-delete": true,
        "max-filename-chars": 120
      },
      on: { change: this.helpers.fileStatusChanged, delete: this.helpers.removeSelectedFile }
    }), userFile && userFile.error ? /* @__PURE__ */ jsx(TooltipEl, {
      attrs: {
        "always-show": true,
        type: DisplayType.Error,
        placement: Placement.Right
      }
    }, userFile.errorMsg || `Please select a CSV file`) : null), /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-bulk-add-users-subtitle"
    }, "Please follow the format of our", /* @__PURE__ */ jsx("a", {
      attrs: {
        href: `https://cdn.mxpnl.com/static/organizations/bulk_add_template.csv`,
        download: `bulk_adding_template.csv`
      }
    }, "CSV template")), /* @__PURE__ */ jsx(FooterButtons, null));
  };
  const ServiceAccount = () => {
    return /* @__PURE__ */ jsx(null, null, error ? /* @__PURE__ */ jsx(BannerEl, {
      sel: ".invite-users-error-banner",
      attrs: {
        theme: `warn`,
        type: BannerType.Embedded,
        "header-text": `There was an error creating the service account.`,
        "body-text": error
      }
    }) : null, !serviceAccount ? /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-add-users"
    }, userRows.map((row, idx) => /* @__PURE__ */ jsx(InviteUsersRow, {
      row,
      idx
    }))), /* @__PURE__ */ jsx("div", {
      sel: ".mp-modal-bottom-buttons"
    }, /* @__PURE__ */ jsx(ButtonEl, {
      sel: ".mp-modal-button",
      attrs: {
        theme: ButtonTheme.Primary,
        disabled: isLoading || !this.attr(`open`) || this.helpers.hasInvalidName()
      },
      on: { click: this.helpers.clickedSave }
    }, "Create"), /* @__PURE__ */ jsx(ButtonEl, {
      sel: ".mp-modal-button",
      on: { click: this.helpers.clickedCancel }
    }, "Cancel"))) : /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
      sel: ".copy-container"
    }, /* @__PURE__ */ jsx("div", {
      sel: ".copy-token-warning.mp-font-weight-bold"
    }, "Make sure you save the service account's secret. You will never be able to access it again."), /* @__PURE__ */ jsx("div", {
      sel: ".modal-content-container"
    }, /* @__PURE__ */ jsx("div", {
      sel: ".modal-content-row"
    }, /* @__PURE__ */ jsx(CopyToClipboardEl, {
      attrs: {
        label: `Username`,
        value: serviceAccount.username
      }
    })), /* @__PURE__ */ jsx("div", {
      sel: ".modal-content-row"
    }, /* @__PURE__ */ jsx(CopyToClipboardEl, {
      attrs: {
        label: `Secret`,
        value: serviceAccount.secret
      }
    })))), /* @__PURE__ */ jsx("div", {
      sel: ".mp-modal-bottom-buttons"
    }, /* @__PURE__ */ jsx(ButtonEl, {
      sel: ".mp-modal-button",
      on: { click: this.helpers.handleClickedDone }
    }, "Done"))));
  };
  const AddAnotherUser = () => {
    return /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-add-another-user"
    }, /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-add-another-user-button",
      on: { click: this.helpers.clickedAddMoreUser }
    }, /* @__PURE__ */ jsx(SvgIconEl, {
      attrs: { icon: `plus` }
    }), "Invite Another Person"), /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-add-another-user-alignment"
    }));
  };
  const OrgInvite = () => {
    return /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-to-org"
    }, userRows.map((row, idx) => /* @__PURE__ */ jsx(InviteUsersRow, {
      row,
      idx
    })), /* @__PURE__ */ jsx(AddAnotherUser, null), /* @__PURE__ */ jsx(FooterButtons, null));
  };
  const TeamInvite = () => {
    return /* @__PURE__ */ jsx("div", {
      sel: ".invite-users-to-teams"
    }, userRows.map((row, idx) => /* @__PURE__ */ jsx(InviteUsersRow, {
      row,
      idx
    })), /* @__PURE__ */ jsx(AddAnotherUser, null), /* @__PURE__ */ jsx(FooterButtons, null));
  };
  const ModalContent = () => {
    var _a, _b;
    if (this.isServiceAccount()) {
      return /* @__PURE__ */ jsx(ServiceAccount, null);
    }
    if (this.isOverSeatCount()) {
      return /* @__PURE__ */ jsx("div", {
        sel: ".invite-users-warning-text-container"
      }, /* @__PURE__ */ jsx(SvgIconEl, {
        attrs: { icon: `warning` }
      }), /* @__PURE__ */ jsx("div", {
        sel: ".invite-users-warning-text"
      }, "You have run out of seats in your organization!", this.state.org.permissions.includes(Permission.UpdateOrgBilling) ? /* @__PURE__ */ jsx(null, null, "Click", /* @__PURE__ */ jsx("a", {
        sel: "#pricing-link",
        attrs: { href: `/pricing` },
        on: { click: this.helpers.trackUpsellClick }
      }, "here"), "to upgrade your plan.") : `Contact your organization's billing admin or owner to upgrade your plan.`));
    }
    switch (this.inviteType) {
      case Tab.Org:
        return /* @__PURE__ */ jsx(OrgInvite, null);
      case Tab.Team:
        return /* @__PURE__ */ jsx(TeamInvite, null);
      case Tab.Bulk:
        return /* @__PURE__ */ jsx(BulkInvite, null);
      case Tab.Project:
        return /* @__PURE__ */ jsx(ProjectInviteModalContentEl, {
          params: {
            orgId: this.state.org.id,
            projectId: this.state.project.id,
            projectRoles: (_a = this.state.projectRoles) == null ? void 0 : _a.projects[this.state.project.id],
            orgMembers: this.state.orgUsers,
            projectMembers: (_b = this.state.projectMembers) == null ? void 0 : _b.members,
            orgDomains: this.state.orgDomains,
            onDone: this.helpers.handleClickedDone
          }
        });
      default:
        assertUnreachable(this.inviteType);
    }
  };
  return /* @__PURE__ */ jsx(ModalEl, {
    sel: ".invite-users-modal",
    attrs: { open: this.attr(`open`) },
    on: { change: this.helpers.modalVisibilityChanged }
  }, /* @__PURE__ */ jsx("div", {
    sel: ".invite-users-container"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".invite-users-header-container"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".mp-modal-title"
  }, this.getTitle()), /* @__PURE__ */ jsx(ButtonEl, {
    on: { click: this.helpers.clickedCancel },
    attrs: { icon: `x`, theme: ButtonTheme.Tertiary }
  })), isLoading ? /* @__PURE__ */ jsx(SpinnerEl, {
    attrs: { color: SpinnerColor.White }
  }) : /* @__PURE__ */ jsx(ModalContent, null)));
}
